




















import { mapState } from 'vuex'
import { PropType } from '@nuxtjs/composition-api'
import { AdaptiveImage } from '@/contracts/AdaptiveImage'
import { Logger } from '@/composition/Logger'
const WEBP_CONTENT_TYPE = 'image/webp'
const PNG_CONTENT_TYPE = 'image/png'
const WEBP_URL_FORMAT = 'webp'
const PNG_URL_FORMAT = 'png'

export default {
  props: {
    sm: {
      type: Object as PropType<AdaptiveImage>,
      default: () => ({}),
    },
    md: {
      type: Object as PropType<AdaptiveImage>,
      default: () => ({}),
    },
    lg: {
      type: Object as PropType<AdaptiveImage>,
      default: () => ({}),
    },
    xl: {
      type: Object as PropType<AdaptiveImage>,
      default: () => ({}),
    },
    alt: {
      type: [String, Number],
      default: '',
    },
    isLazy: {
      type: Boolean,
      default: true,
    },
    isCover: {
      type: Boolean,
      default: true,
    },
    isLocal: {
      type: Boolean,
      default: false,
    },
    quality: {
      type: Number,
      default: 100,
    },
    multiplier: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      sources: [],
      fallback: '',
      rootUrl: this.$config.IMAGE_OPTIMIZER_URL,
      webpContentType: WEBP_CONTENT_TYPE,
      pngContentType: PNG_CONTENT_TYPE,
      webpUrlFormat: WEBP_URL_FORMAT,
      pngUrlFormat: PNG_URL_FORMAT,
    }
  },

  computed: {
    ...mapState(['mediaQuery']),
    mqList() {
      return [
        { key: 'sm', min: 0, max: 767 },
        { key: 'md', min: 768, max: 1023 },
        { key: 'lg', min: 1024, max: 1439 },
        { key: 'xl', min: 1440 },
      ]
    },
    mqLargest() {
      return this.mqList[this.mqList.length - 1].key
    },
    style() {
      const mqKey = this.mediaQuery.mqKeyLarge || this.mediaQuery.mqKey
      return {
        '--object-position':
          this.adaptive[mqKey]?.imagePosition || 'center center',
        '--object-fit': this.isCover ? 'cover' : 'none',
        width: this.adaptive[mqKey]?.size?.[0] || '100%',
        height: this.adaptive[mqKey]?.size?.[1] || 'auto',
      }
    },
    adaptive() {
      return {
        sm: this.sm,
        md: this.md,
        lg: this.lg,
        xl: this.xl,
      }
    },
    sizes() {
      return {
        sm: this.sm?.imageSize || [],
        md: this.md?.imageSize || [],
        lg: this.lg?.imageSize || [],
        xl: this.xl?.imageSize || [],
      }
    },
    imgWidth() {
      if (!this.sizes) return undefined
      const mqKey = this.mediaQuery.mqKeyLarge || this.mediaQuery.mqKey
      if (mqKey in this.sizes) return this.sizes[mqKey][0]
      if (this.mqLargest in this.sizes) return this.sizes[this.mqLargest][0]
      return undefined
    },
    imgHeight() {
      if (!this.sizes) return undefined
      const mqKey = this.mediaQuery.mqKeyLarge || this.mediaQuery.mqKey
      if (mqKey in this.sizes) return this.sizes[mqKey][1]
      if (this.mqLargest in this.sizes) return this.sizes[this.mqLargest][1]
      return undefined
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.sources = []
      this.fallback = {}
      // eslint-disable-next-line
      const regExp = /^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n?]+)/g

      Object.keys(this.adaptive).forEach((key) => {
        const mq = this.adaptive[key]
        if (!mq?.imageUrl) return
        this.setSize(mq.imageUrl.replace(regExp, ''), key, key === 'xl')
      })
    },
    getUrl(sizes, format = 'webp', source, isLqi = false) {
      let result = source

      if (!this.isLocal) {
        const coverParam = this.isCover ? '&fit=cover' : ''
        const lqiParam = '&blur=10'

        result = `${this.rootUrl}${source}?format=${format}&w=${sizes[0]}&h=${
          sizes[1]
        }&q=${isLqi ? 50 : this.quality}${isLqi ? lqiParam : coverParam}`
      }

      return result
    },
    setSize(source, mq, isFallback) {
      if (!this.sizes) {
        Logger.error('we need 404 image url here')
        return
      }

      const szLargest = [
        this.sizes[this.mqLargest][0] * this.multiplier,
        this.sizes[this.mqLargest][1] * this.multiplier,
      ]

      const sz = [
        this.sizes[mq][0] * this.multiplier,
        this.sizes[mq][1] * this.multiplier,
      ]
      const sz2x = [
        this.sizes[mq][0] * this.multiplier * 2,
        this.sizes[mq][1] * this.multiplier * 2,
      ]

      const webpSrc = this.getUrl(sz, this.webpUrlFormat, source)

      const webpSrc2x = this.getUrl(sz2x, this.webpUrlFormat, source)

      const srcsetWebp = `${webpSrc}, ${webpSrc2x} 2x`

      const pngSrc = this.getUrl(sz, this.pngUrlFormat, source)

      const pngSrc2x = this.getUrl(sz2x, this.pngUrlFormat, source)

      const srcsetPng = `${pngSrc}, ${pngSrc2x} 2x`

      const mediaSize = this.mqList.find((item) => item.key === mq)

      let media = `(min-width: ${mediaSize.min || 0}px)`
      if (mediaSize.max) {
        media += ` and (max-width: ${mediaSize.max || 0}px)`
      }

      this.sources.push({
        srcset: srcsetWebp,
        type: this.webpContentType,
        media,
      })

      this.sources.push({
        srcset: srcsetPng,
        type: this.pngContentType,
        media,
      })

      if (isFallback) {
        this.fallback = this.getUrl(szLargest, this.webpUrlFormat, source)
      }
    },
  },
}
